import React, { useEffect, useState } from "react";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SectionHeading } from "@/components/blocks";
import { LotusDivider } from "@/components/Botanical";
import { api } from "@/lib/api";

export default function Testimonials({ dark = false }) {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setActive((a) => (a + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;
  const t = items[active];
  const go = (d) => setActive((a) => (a + d + items.length) % items.length);

  return (
    <section data-testid="testimonials-section" className={`${dark ? "bg-charcoal" : "bg-cream"} py-20 md:py-28`}>
      <div className="container-clinic">
        <Reveal><SectionHeading center light={dark} overline="In Their Words" title="Stories of care" subtitle="What women say about their experience with Dr. Mithee." /></Reveal>
        <LotusDivider className="mt-6" />

        <Reveal delay={100} className="relative mx-auto mt-12 max-w-3xl">
          <figure key={t.id} data-testid="testimonial-active" className={`animate-fade-up rounded-[2.5rem] border p-8 text-center shadow-card md:p-14 ${dark ? "border-cream/10 bg-cream/[0.05]" : "border-soft-border bg-background"}`}>
            <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-blush text-terracotta"><Quote className="h-6 w-6" /></span>
            <blockquote className={`mt-6 font-serif text-xl leading-relaxed md:text-2xl ${dark ? "text-cream" : "text-charcoal"}`}>“{t.content}”</blockquote>
            <div className="mt-6 flex justify-center gap-1 text-gold">
              {[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-gold" />)}
            </div>
            <figcaption className="mt-4">
              <p className={`font-sans text-sm font-semibold ${dark ? "text-cream" : "text-charcoal"}`}>{t.name}</p>
              {t.role && <p className="mt-0.5 text-xs uppercase tracking-[0.2em] text-terracotta">{t.role}</p>}
            </figcaption>
          </figure>

          {items.length > 1 && (
            <>
              <button onClick={() => go(-1)} aria-label="Previous testimonial" data-testid="testimonial-prev" className="absolute -left-2 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-soft-border bg-cream text-terracotta shadow-soft transition-transform hover:scale-105 md:-left-14"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => go(1)} aria-label="Next testimonial" data-testid="testimonial-next" className="absolute -right-2 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-soft-border bg-cream text-terracotta shadow-soft transition-transform hover:scale-105 md:-right-14"><ChevronRight className="h-5 w-5" /></button>
              <div className="mt-8 flex justify-center gap-2" data-testid="testimonial-dots">
                {items.map((it, i) => (
                  <button key={it.id} onClick={() => setActive(i)} aria-label={`Testimonial ${i + 1}`} className={`h-2 rounded-full transition-[width,background-color] duration-300 ${i === active ? "w-8 bg-terracotta" : "w-2 bg-terracotta/30"}`} />
                ))}
              </div>
            </>
          )}
        </Reveal>
      </div>
    </section>
  );
}
